import { useEffect, useRef, useState } from "react";
import { MagnifyingGlass, X } from "@phosphor-icons/react";
import { api } from "../lib/api";

/**
 * All-in-one symbol search — hits `/api/tv-udf/search` which fans out across
 *   1. Our 16 tradable Tradelog symbols (top of the list)
 *   2. 500+ static popular universe (stocks, ETFs, indices, crypto, forex, commodities)
 *   3. TwelveData's ~100k global ticker DB (long tail)
 *
 * On selection it emits `onPick(symbolObject)` so the parent can jump the
 * chart to the picked ticker. Keyboard navigation (↑ ↓ Enter Esc), debounced
 * fetch, empty-state help — everything the user expects from a TradingView
 * search box.
 */
export default function SymbolSearch({ onPick, placeholder = "Search any symbol — AAPL, TSLA, EURUSD, XAUUSD, NIFTY…" }) {
  const [q, setQ] = useState("");
  const [open, setOpen] = useState(false);
  const [results, setResults] = useState([]);
  const [active, setActive] = useState(0);
  const [loading, setLoading] = useState(false);
  const wrapRef = useRef(null);
  const inputRef = useRef(null);

  // Close on outside click / Esc
  useEffect(() => {
    if (!open) return;
    const onDown = (e) => { if (wrapRef.current && !wrapRef.current.contains(e.target)) setOpen(false); };
    const onEsc = (e) => e.key === "Escape" && setOpen(false);
    document.addEventListener("mousedown", onDown);
    document.addEventListener("keydown", onEsc);
    return () => { document.removeEventListener("mousedown", onDown); document.removeEventListener("keydown", onEsc); };
  }, [open]);

  // Debounced fetch
  useEffect(() => {
    if (!q.trim()) { setResults([]); return; }
    const t = setTimeout(async () => {
      setLoading(true);
      try {
        const { data } = await api.get(`/tv-udf/search?query=${encodeURIComponent(q.trim())}&limit=20`);
        setResults(Array.isArray(data) ? data : []);
        setActive(0);
      } catch (_) { setResults([]); }
      setLoading(false);
    }, 180);
    return () => clearTimeout(t);
  }, [q]);

  const pick = (r) => {
    onPick && onPick(r);
    setOpen(false); setQ(""); setResults([]);
  };

  const onKey = (e) => {
    if (!open) return;
    if (e.key === "ArrowDown") { e.preventDefault(); setActive((a) => Math.min(a + 1, results.length - 1)); }
    else if (e.key === "ArrowUp") { e.preventDefault(); setActive((a) => Math.max(a - 1, 0)); }
    else if (e.key === "Enter" && results[active]) { e.preventDefault(); pick(results[active]); }
  };

  const typeColor = {
    stock: "#22d3ee", etf: "#a78bfa", index: "#10b981", crypto: "#f7931a",
    forex: "#3b82f6", commodity: "#f5c518", metals: "#f5c518", futures: "#ef4444",
  };

  return (
    <div ref={wrapRef} className="relative w-full max-w-xl" data-testid="symbol-search">
      <div className="flex items-center gap-2 h-10 px-3 rounded-full border border-border bg-secondary/40 focus-within:border-primary/60 transition">
        <MagnifyingGlass size={14} className="text-muted-foreground shrink-0" />
        <input
          ref={inputRef}
          type="text"
          value={q}
          onChange={(e) => { setQ(e.target.value); setOpen(true); }}
          onFocus={() => q && setOpen(true)}
          onKeyDown={onKey}
          placeholder={placeholder}
          className="flex-1 bg-transparent border-0 outline-none text-sm text-foreground placeholder:text-muted-foreground"
          data-testid="symbol-search-input" />
        {q && (
          <button onClick={() => { setQ(""); inputRef.current?.focus(); }} className="text-muted-foreground hover:text-foreground" data-testid="symbol-search-clear">
            <X size={13} />
          </button>
        )}
      </div>

      {open && (q || results.length > 0) && (
        <div
          className="absolute left-0 right-0 mt-2 rounded-2xl border border-border bg-popover/95 backdrop-blur-xl shadow-2xl z-40 overflow-hidden"
          data-testid="symbol-search-panel">
          {loading && <div className="px-3 py-3 text-xs text-muted-foreground">Searching…</div>}
          {!loading && q && results.length === 0 && (
            <div className="px-3 py-4 text-xs text-muted-foreground">No matches for <span className="text-foreground font-semibold">{q}</span>. Try a full ticker like AAPL, EURUSD, XAUUSD.</div>
          )}
          <ul className="max-h-96 overflow-y-auto py-1" data-testid="symbol-search-list">
            {results.map((r, i) => (
              <li key={`${r.exchange}:${r.symbol}:${i}`}>
                <button
                  type="button"
                  onMouseEnter={() => setActive(i)}
                  onClick={() => pick(r)}
                  className={`w-full flex items-center gap-3 px-3 py-2.5 text-left transition text-sm ${i === active ? "bg-white/[0.06]" : ""}`}
                  data-testid={`symbol-result-${r.symbol}`}>
                  <span className="text-[10px] font-black px-1.5 py-0.5 rounded uppercase tracking-widest"
                    style={{ background: (typeColor[r.type] || "#64748b") + "22", color: typeColor[r.type] || "#64748b" }}>
                    {r.type || "sym"}
                  </span>
                  <span className="font-mono font-bold text-foreground min-w-[70px]">{r.symbol}</span>
                  <span className="text-muted-foreground truncate flex-1">{r.description}</span>
                  <span className="text-[10px] text-muted-foreground uppercase tracking-widest">{r.exchange}</span>
                </button>
              </li>
            ))}
          </ul>
        </div>
      )}
    </div>
  );
}
